import { blue, gray, green, orange, purple, red } from '@ant-design/colors';
import { notification } from 'antd';
import type { TotalizadorListData } from '@/components/totalizador/TotalizadorList';
import type { ApiResponseStatus } from '@/std/api/types';
import type { SimNao } from '@/types';
import type { SortParams } from '@/types/interfaces';
import {
    canUserListAllVendedores,
    getEmpresa,
    getEmpresaIdpk,
    getLogin,
} from '@/features/login/util';
import { marcadoresToStringIdpk } from '@/features/marcador/util';
import { NotaFiscalStatus } from '@/features/nfe/types/enum';
import { type RootDispatch, type RootState, store } from '@/state/store';
import { type BuildUrlParams, buildUrl } from '@/std/api/buildUrl';
import { comTokenGet, comTokenPost, comTokenPut, comTokenRemove } from '@/std/api/comToken';
import { isResponseOk, throwIfResponseIsErr } from '@/std/api/util';
import { endReduxFnError, endReduxFnOk, startReduxFn } from '@/std/redux';
import { TipoConsulta } from '@/types/enum';
import { Endpoint } from '@/types/enum/endpoint';
import type { TCupomFiscal, TCupomFiscalTotais } from './types';

export const effects = (dispatch: RootDispatch) => ({
    async cancelarCupomFiscal(
        payload: {
            nof_idpk: number;
            justificativa: string;
            cancelar_venda?: SimNao;
            updateTable?: boolean;
        },
        state: RootState,
    ): Promise<boolean> {
        dispatch.cuponsFiscais.setState({
            cancelarCupomFiscal: startReduxFn(state.cuponsFiscais.cancelarCupomFiscal.data),
        });

        try {
            const { nof_idpk, updateTable, ...rest } = payload;

            const params = {
                ...rest,
                empresa_idpk: getEmpresaIdpk(),
            };

            const url = buildUrl(Endpoint.NFCeCancelar, params, nof_idpk);
            const response = await comTokenPost(url);
            throwIfResponseIsErr(response);

            const { data } = response;

            if (data) {
                const { cuponsFiscais } = state;
                const {
                    getTable,
                    getTable: { selectedRow },
                } = cuponsFiscais;

                if (selectedRow) {
                    const newSelectedRow = structuredClone(selectedRow);

                    newSelectedRow.nof_nfe_pdf = data.evento_pdf;
                    newSelectedRow.nof_xml = data.evento_xml;

                    dispatch.cuponsFiscais.setState({
                        getTable: {
                            ...getTable,
                            selectedRow: newSelectedRow,
                        },
                    });
                }
            }

            dispatch.cuponsFiscais.setState({
                cancelarCupomFiscal: endReduxFnOk(data),
            });

            if (updateTable) {
                void dispatch.cuponsFiscais.get({});
            }

            return true;
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                cancelarCupomFiscal: endReduxFnError(error),
            });

            return false;
        }
    },

    async compartilharSend(
        payload: {
            idpk: number;
            tipo: string;
            template_id: string;
            tags?: { nome: string; valor?: string | null }[];
            fin_email?: string | null;
            fin_email_cc?: string | null;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.cuponsFiscais.setState({
            compartilharSend: startReduxFn(state.cuponsFiscais.compartilharSend.data),
        });

        const { idpk, tags, fin_email, fin_email_cc, ...rest } = payload;

        const params = {
            empresa_idpk: getEmpresaIdpk(),
            ...rest,
        };
        const bodySend = payload.tipo !== 'W' ? { fin_email, fin_email_cc, tags } : undefined;

        const url = buildUrl(Endpoint.NFCeCompartilhar, params, idpk);

        try {
            const response = await comTokenPost(url, bodySend);
            throwIfResponseIsErr(response);

            dispatch.cuponsFiscais.setState({
                compartilharSend: endReduxFnOk(null),
                showDrawerCompartilhar: false,
            });

            if (payload.tipo === 'E') {
                notification.success({
                    description:
                        'Atenção, a notificação pode demorar até 5 minutos para ser entregue.',
                    title: 'Email enviado',
                });
            }
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                compartilharSend: endReduxFnError(error),
            });

            if (payload.tipo === 'E') {
                notification.error({
                    description: error.message,
                    title: 'Falhou!',
                });
            }
        }
    },

    async emitirCupomFiscal(
        payload: {
            itens: TCupomFiscal[];
            sendToPrint?: boolean;
            updateTable?: boolean;
        },
        state: RootState,
    ): Promise<ApiResponseStatus> {
        const setState = dispatch.cuponsFiscais.setState;

        setState({
            emitirCupomFiscal: {
                cancel: false,
                data: null,
                error: null,
                loading: true,
            },
        });

        const { itens, updateTable } = payload;
        const params = { empresa_idpk: getEmpresaIdpk() };

        let hasError = false;

        const notasComErro: Array<{
            nof_idpk: number;
            nof_numero: number;
            motivoErro: string;
        }> = [];

        const idpksSuccess: number[] = [];

        try {
            // TODO trocar async for por Promisse.All
            // for (const [index, item] of itens.entries()) {
            for (const [index, item] of itens.entries()) {
                const nof_idpk = item.nof_idpk;
                if (!nof_idpk) {
                    continue;
                }

                const { cuponsFiscais } = store.getState() as RootState;

                const {
                    emitirCupomFiscal: { cancel },
                    getTable,
                } = cuponsFiscais || {};

                if (cancel) {
                    item.emitirCancel = true;

                    setState({
                        getTable: {
                            ...getTable,
                            selectedRows: [
                                ...itens.slice(0, index),
                                item,
                                ...itens.slice(index + 1),
                            ] as TCupomFiscal[],
                        },
                    });

                    hasError = true;

                    throw new Error('Emissão da nota fiscal cancelada pelo usuário');
                }

                item.loading = true;

                setState({
                    getTable: {
                        ...getTable,
                        selectedRows: [...itens.slice(0, index), item, ...itens.slice(index + 1)],
                    },
                });

                const url = buildUrl(Endpoint.NFCeEnviar, params, nof_idpk);
                const response = await comTokenPost(url);

                if (!isResponseOk(response)) {
                    const apiErrorMessage = response?.data?.informacao;

                    item.nof_status = NotaFiscalStatus.Incorreta;
                    item.loading = false;

                    if (apiErrorMessage) {
                        item.motivoErro = apiErrorMessage
                            .map((error) => error?.mensagem)
                            .filter(Boolean)
                            .join('\n');
                    } else {
                        item.motivoErro = response?.data?.mensagem;
                    }

                    setState({
                        getTable: {
                            ...getTable,
                            selectedRows: [
                                ...itens.slice(0, index),
                                item,
                                ...itens.slice(index + 1),
                            ],
                        },
                    });

                    hasError = true;

                    notasComErro.push({
                        motivoErro: item.motivoErro || '',
                        nof_idpk,
                        nof_numero: response?.data?.nof_numero || 0,
                    });

                    continue;
                }

                const {
                    motivo,
                    mensagem = 'Falha ao tentar emitir a nota fiscal!',
                    nof_nfe_pdf = '',
                    nof_xml = '',
                    nof_status = NotaFiscalStatus.Emitida,
                } = response?.data || {};

                if (nof_status !== NotaFiscalStatus.Emitida) {
                    item.nof_status = nof_status;
                    item.loading = false;
                    item.motivoErro = motivo || mensagem;

                    setState({
                        getTable: {
                            ...getTable,
                            selectedRows: [
                                ...itens.slice(0, index),
                                item,
                                ...itens.slice(index + 1),
                            ],
                        },
                    });

                    notasComErro.push({
                        motivoErro: item.motivoErro || '',
                        nof_idpk,
                        nof_numero: response?.data?.nof_numero || 0,
                    });

                    hasError = true;

                    continue;
                }

                item.nof_nfe_pdf = nof_nfe_pdf;
                item.nof_xml = nof_xml;
                item.nof_status = nof_status;
                item.loading = false;
                item.emitirOk = true;

                setState({
                    getTable: {
                        ...getTable,
                        selectedRows: [...itens.slice(0, index), item, ...itens.slice(index + 1)],
                    },
                });

                idpksSuccess.push(nof_idpk);
            }

            const { cuponsFiscais } = state;
            const { showDrawerEmitirNf, getTable } = cuponsFiscais || {};

            if (showDrawerEmitirNf) {
                setState({
                    emitirCupomFiscal: {
                        cancel: false,
                        data: hasError ? notasComErro : 'ok',
                        error: hasError ? 'Houve um erro em alguma das notas' : null,
                        loading: false,
                    },
                });
            } else {
                const ajustRowsSelected = itens
                    .filter((row) => !row.emitirOk)
                    .map((row) => {
                        row.emitirCancel = false;
                        row.emitirOk = false;
                        row.motivoErro = undefined;
                        row.loading = false;
                        return row;
                    });

                setState({
                    emitirCupomFiscal: {
                        cancel: false,
                        data: 'ok',
                        error: null,
                        loading: false,
                    },
                    getTable: {
                        ...getTable,
                        selectedRows: ajustRowsSelected,
                    },
                });
            }

            if (updateTable) {
                void dispatch.cuponsFiscais.get({});
            }

            return 'sucesso';
        } catch (error) {
            setState({
                emitirCupomFiscal: {
                    cancel: false,
                    data: 'ok',
                    error,
                    loading: false,
                },
            });

            return 'erro';
        }
    },
    async get(
        payload: {
            pesquisar?: string;
            data_inicio?: string;
            data_fim?: string;
            total_registros?: string;
            registro_inicial?: number;
            qtde_registros?: number;
            sort?: SortParams;
            tipo_consulta?: TipoConsulta;
            idpk?: number;
        },
        state: RootState,
    ): Promise<void> {
        const { cuponsFiscais } = state;
        const { getTable, get } = cuponsFiscais;

        dispatch.cuponsFiscais.setState({
            get: startReduxFn(get.data),
            getTable: {
                ...getTable,
                updateTable: false,
            },
        });

        try {
            const {
                filterDateRange,
                filterPesquisar,
                filterTipo,
                filterFornecedor,
                filterCliente,
                filterNumero,
                filterSerie,
                filterStatus,
                filterVendedor,
                filterCidade,
                filterMarcador,
                filterAcao,
                filterSistemasIntegrados,
                registroInitial,
                qtdRegistros,
                sortParams,
                pagination,
                resetPagination,
                filterFormasPagamento,
            } = getTable;

            const {
                pesquisar,
                total_registros,
                registro_inicial,
                qtde_registros,
                sort,
                tipo_consulta = TipoConsulta.Resumida,
                idpk,
                ...restPayload
            } = payload;

            const ambiente = getEmpresa()?.emp_fiscal_ambiente;

            // MONTANDO OS PARAMETROS OBRIGATÓRIOS
            const params: BuildUrlParams = {
                acao: filterAcao,
                ambiente,
                data_fim: filterDateRange?.end.format('DD-MM-YYYY'),
                data_inicio: filterDateRange?.start.format('DD-MM-YYYY'),
                empresa_idpk: getEmpresaIdpk(),
                forma_pagamento_idpk: filterFormasPagamento?.map((f) => f.fop_idpk),
                marcadores: marcadoresToStringIdpk(filterMarcador),
                orderby: 'nof_data_emissao desc, nof_idpk desc',
                qtde_registros: qtde_registros || qtdRegistros,
                registro_inicial:
                    registro_inicial !== null && registro_inicial !== undefined
                        ? registro_inicial
                        : registroInitial,
                sistema_integrado_idpk: filterSistemasIntegrados?.map((s) => s.sistema_idpk),
                tipo_consulta,
                ...restPayload,
            };

            // CASO HOUVEREM FILTROS OPCIONAIS
            if (filterStatus) {
                params.status = JSON.stringify(filterStatus);
            }

            if (filterTipo) {
                params.tipo = filterTipo.idpk;
            }

            if (!canUserListAllVendedores()) {
                params.vendedor_idpk = getLogin()?.usu_idpk;
            } else if (filterVendedor) {
                params.vendedor_idpk = filterVendedor.idpk;
            }

            if (filterCidade) {
                params.destinatario_cidade = filterCidade?.nome || '';
            }

            if (filterNumero) {
                params.numero = filterNumero;
            }

            if (filterSerie) {
                params.serie = filterSerie;
            }

            if (pesquisar || filterPesquisar) {
                params.pesquisar = pesquisar || filterPesquisar;
            }

            // filtros que usam param 'where'
            let where = '';

            if (filterFornecedor) {
                where = `nof_fornecedor_idpk=${filterFornecedor.idpk}`;
            }

            if (filterCliente) {
                where = `${where.length ? `${where} AND ` : ''}nof_cliente_idpk=${filterCliente.idpk}`;
            }

            if (idpk) {
                where = `${where.length ? `${where} AND ` : ''}nof_idpk=${idpk}`;
            }

            if (where.length) {
                params.where = where;
            }

            // CASO TENHA QUE ORDENAR OS DADOS (*OPCIONAL)
            if (sort?.shouldSort || sortParams?.shouldSort) {
                params.orderby = `${sort?.fieldName || sortParams?.fieldName}${
                    sort?.orderDirection || sortParams?.orderDirection
                }`;
            }

            // CASO TIVER ENVIADO PARA MOSTRAR TODOS REGISTROS
            if (total_registros) {
                params.total_registros = total_registros;
            }

            // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
            if (resetPagination) {
                params.registro_inicial = 0;
                params.total_registros = 'S';
            }

            const url = buildUrl(Endpoint.NFCeConsultar, params);
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [], total_registros: totalRegistroResponse = null } = {} } =
                response;

            dispatch.cuponsFiscais.setState({
                get: endReduxFnOk(registros),
                getTable: {
                    ...getTable,
                    updateTable: false,
                    ...((totalRegistroResponse || totalRegistroResponse === 0) && {
                        totalRegistrosTable: totalRegistroResponse,
                    }),
                    // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
                    ...(resetPagination && {
                        registro_inicial: 0,
                    }),
                    pagination: {
                        ...pagination,
                        // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
                        ...(resetPagination && {
                            current: 1,
                        }),
                        // SE PRECISAR ATUALIZAR A PÁGINA É FEITO AQUI
                        ...(registro_inicial !== null &&
                            registro_inicial !== undefined && {
                                current: registro_inicial / (qtde_registros || qtdRegistros) + 1,
                            }),
                        // SE PRECISAR ATUALIZAR OS TOTAIS É FEITO AQUI
                        ...((totalRegistroResponse || totalRegistroResponse === 0) && {
                            showSizeChanger: totalRegistroResponse > 10,
                            showTotal: () => `Total de Registros: ${totalRegistroResponse}`,
                            total: totalRegistroResponse,
                        }),
                    },
                    resetPagination: false,
                },
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                get: endReduxFnError(error),
            });
        }
    },

    async getOne(payload: { nof_idpk: number }, state: RootState): Promise<TCupomFiscal> {
        dispatch.cuponsFiscais.setState({
            getOne: startReduxFn(state.cuponsFiscais.getOne.data),
        });

        const { nof_idpk } = payload;
        const params = { empresa_idpk: getEmpresaIdpk() };

        const url = buildUrl(Endpoint.NFCeConsultar, params, nof_idpk);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;

            const returnGetOne = registros && registros.length > 0 ? registros[0] : null;

            dispatch.cuponsFiscais.setState({
                getOne: endReduxFnOk(returnGetOne),
            });

            return returnGetOne;
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                getOne: endReduxFnError(error),
            });
            return error;
        }
    },

    async lançarEstoque(payload: {
        idpk: number;
        movimentar?: string;
        estornar?: string;
    }): Promise<void> {
        dispatch.cuponsFiscais.setState({
            lancarEstoque: startReduxFn(),
        });

        const { idpk, movimentar, estornar } = payload;

        const empresaIdpk = getEmpresaIdpk();
        if (!empresaIdpk) {
            return;
        }

        let params: { empresa_idpk: number; movimentar?: string; estornar?: string } = {
            empresa_idpk: empresaIdpk,
        };

        if (movimentar) {
            params = { ...params, movimentar };
        } else if (estornar) {
            params = { ...params, estornar };
        }

        const url = buildUrl(Endpoint.NFCeEstoque, params, idpk);

        try {
            const response = await comTokenPut(url, null);
            throwIfResponseIsErr(response);

            dispatch.cuponsFiscais.setState({
                lancarEstoque: endReduxFnOk(null),
            });

            notification.success({
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                lancarEstoque: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async lançarFinanceiro(payload: {
        idpk: number;
        movimentar?: string;
        estornar?: string;
    }): Promise<void> {
        dispatch.cuponsFiscais.setState({
            lancarFinanceiro: startReduxFn(),
        });

        const { idpk, movimentar, estornar } = payload;

        const empresaIdpk = getEmpresaIdpk();
        if (!empresaIdpk) {
            return;
        }

        let params: { empresa_idpk: number; movimentar?: string; estornar?: string } = {
            empresa_idpk: empresaIdpk,
        };

        if (movimentar) {
            params = { ...params, movimentar };
        } else if (estornar) {
            params = { ...params, estornar };
        }

        const url = buildUrl(Endpoint.NFCeFinanceiro, params, idpk);

        try {
            const response = await comTokenPut(url, null);
            throwIfResponseIsErr(response);

            dispatch.cuponsFiscais.setState({
                lancarFinanceiro: endReduxFnOk(null),
            });

            notification.success({
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                lancarFinanceiro: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async post(
        payload: { body: TCupomFiscal; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        dispatch.cuponsFiscais.setState({
            post: startReduxFn(state.cuponsFiscais.post.data),
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NFCeInserir, params);
            const response = await comTokenPost(url, payload.body);
            throwIfResponseIsErr(response);

            const nof_idpk: number = response.data.registros?.[0]
                ? response.data.registros[0].nof_idpk
                : 0;

            dispatch.cuponsFiscais.setState({
                post: endReduxFnOk({ nof_idpk }),
            });

            if (payload?.updateTable) {
                void dispatch.cuponsFiscais.get({});
            }

            notification.success({
                description: 'Nota Fiscal cadastrada',
                title: 'Feito!',
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                post: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Não foi possível cadastrar a nota fiscal!',
            });
        }
    },

    async put(
        payload: {
            nof_idpk: number;
            body: Partial<TCupomFiscal>;
            updateTable?: boolean;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.cuponsFiscais.setState({
            put: startReduxFn(state.cuponsFiscais.put.data),
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const { nof_idpk, body, updateTable } = payload;
            const url = buildUrl(Endpoint.NFCeAlterar, params, nof_idpk);
            const response = await comTokenPut(url, body);
            throwIfResponseIsErr(response);

            dispatch.cuponsFiscais.setState({
                put: endReduxFnOk('Success'),
            });

            notification.success({
                description: 'Nota Fiscal atualizada',
                title: 'Feito!',
            });

            if (updateTable) {
                void dispatch.cuponsFiscais.get({});
            }
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                put: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Não foi possível atualizar a nota fiscal!',
            });
        }
    },

    async remove(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { cuponsFiscais } = state;
        const { getTable } = cuponsFiscais;

        dispatch.cuponsFiscais.setState({
            getTable: {
                ...getTable,
                loadingTable: true,
            },
            remove: startReduxFn(state.cuponsFiscais.remove.data),
        });

        try {
            const { nof_idpk, updateTable } = payload;
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NFCeRemover, params, nof_idpk);
            const response = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.cuponsFiscais.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                remove: endReduxFnOk('Success'),
            });

            if (updateTable) {
                const {
                    pagination,
                    pagination: { total = 0 },
                    registroInitial,
                } = getTable;

                let isLastPageOnlyOneRegister = false;

                // VERIFICA SE É A ÚLTIMA PÁGINA E TEM APENAS UM ITEM PARA PODER MUDAR DE PÁGINA APÓS DELETAR
                if (total && registroInitial && total - 1 === registroInitial) {
                    isLastPageOnlyOneRegister = true;
                    void dispatch.cuponsFiscais.get({
                        registro_inicial: registroInitial - (pagination?.pageSize || 0),
                        total_registros: 'S',
                    });

                    void dispatch.cuponsFiscais.totalizador({});
                }

                if (!isLastPageOnlyOneRegister) {
                    void dispatch.cuponsFiscais.get({
                        total_registros: 'S',
                    });

                    void dispatch.cuponsFiscais.totalizador({});
                }
            }

            notification.success({
                description: 'Nota Fiscal excluída.',
                title: 'Feito!',
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                remove: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async totalizador(_, state: RootState): Promise<void> {
        const { cuponsFiscais } = state;
        const { totalizador, getTable } = cuponsFiscais;

        dispatch.cuponsFiscais.setState({
            totalizador: startReduxFn(totalizador.data),
        });

        const {
            filterDateRange,
            filterPesquisar,
            filterTipo,
            filterFornecedor,
            filterCliente,
            filterNumero,
            filterSerie,
            filterVendedor,
            filterCidade,
            filterMarcador,
            filterAcao,
            filterSistemasIntegrados,
            filterFormasPagamento,
        } = getTable;

        const ambiente = getEmpresa()?.emp_fiscal_ambiente;

        const params: BuildUrlParams = {
            acao: filterAcao,
            ambiente,
            data_fim: filterDateRange?.end.format('DD-MM-YYYY'),
            data_inicio: filterDateRange?.start.format('DD-MM-YYYY'),
            empresa_idpk: getEmpresaIdpk(),
            forma_pagamento_idpk: filterFormasPagamento?.map((f) => f.fop_idpk),
            marcadores: marcadoresToStringIdpk(filterMarcador),
            pesquisar: filterPesquisar,
            sistema_integrado_idpk: filterSistemasIntegrados?.map((s) => s.sistema_idpk),
        };

        if (filterTipo) {
            params.tipo = filterTipo.idpk;
        }

        if (!canUserListAllVendedores()) {
            params.vendedor_idpk = getLogin()?.usu_idpk;
        } else if (filterVendedor) {
            params.vendedor_idpk = filterVendedor.idpk;
        }

        if (filterCidade) {
            params.destinatario_cidade = filterCidade?.nome;
        }

        if (filterNumero) {
            params.numero = filterNumero;
        }

        if (filterSerie) {
            params.serie = filterSerie;
        }

        // filtros que usam param 'where'
        let where = '';

        if (filterFornecedor) {
            where = `nof_fornecedor_idpk=${filterFornecedor.idpk}`;
        }

        if (filterCliente) {
            where = `${where.length ? `${where} AND ` : ''}nof_cliente_idpk=${filterCliente.idpk}`;
        }

        if (where.length) {
            params.where = where;
        }

        const url = buildUrl(Endpoint.NFCeValoresTotais, params);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;

            const cores = {
                Aguardando: blue.primary,
                Cancelada: red.primary,
                Emitida: green.primary,
                Estornada: gray.primary,
                Incorreta: orange.primary,
                Rejeitada: purple.primary,
                Total: 'black',
            };

            const totais: TotalizadorListData[] = registros.map((total: TCupomFiscalTotais) => ({
                color: cores[total.tipo],
                quantity: total.quantidade_total,
                title: total.tipo,
                total: total.valor_total,
                value: total.tipo,
            }));

            dispatch.cuponsFiscais.setState({
                totalizador: endReduxFnOk(totais),
            });
        } catch (error) {
            dispatch.cuponsFiscais.setState({
                totalizador: endReduxFnError(error),
            });
        }
    },
});
